import OrganizationType from '@/common/models/memberships/OrganizationType.enum';
import QueryParams from '@/common/models/query-params/QueryParams';

const isCurator = (rootState) => rootState.orgApp.organization?.type === OrganizationType.CURATOR;
const isSupplyOrDemand = (rootGetters, rootState) => rootGetters.hasChosenOrganization && !isCurator(rootState);
const isExchangeOrCurator = (rootGetters, rootState) => rootGetters.hasChosenExchange || isCurator(rootState);
const isExchange = (rootGetters) => rootGetters.hasChosenExchange;

const actions = {
  async getDemographics(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = true } = {},
  ) {
    // Moved curator to orgApp after speaking to BE since exchange returns everything for curator and orgApp is returning the expected data
    // For curator at least, the data is returned based off a matching organization id or data provider id
    if (isSupplyOrDemand(rootGetters, rootState) || isCurator(rootState)) {
      return dispatch('orgApp/getDemographics', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchange(rootGetters)) {
      return dispatch('exchApp/getDemographics', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getLanguages(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getLanguages', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getLanguages', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getNetworks(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getNetworks', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getNetworks', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getMediaTypes(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = false } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getMediaTypes', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getMediaTypes', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getGeofenceGroups(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getGeofenceGroups', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getGeofenceGroups', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getLocations(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getLocationsAsync', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getLocations', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getLocationsByLevel(
    { dispatch, rootGetters, rootState },
    { params = {}, cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getLocationsByLevel', { params, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getLocationsByLevel', { params, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getTags(
    { dispatch, rootGetters, rootState },
    { params = {}, cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getTags', { params, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getTags', { params, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getMarkets(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getMarkets', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getMarkets', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getMarketsWithInventory(
    params,
    {
      ownerIDs,
      countryIDs,
      networkIDs = null,
      unitTargeting = true,
      excludeGeometry = true,
      cancelTokenSource = null,
      showLoader = true,
    } = {},
  ) {
    const { dispatch, rootGetters, rootState } = params;
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch(
        'orgApp/getMarketsWithInventory',
        {
          ownerIDs,
          countryIDs,
          networkIDs,
          unitTargeting,
          excludeGeometry,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch(
        'exchApp/getMarketsWithInventory',
        {
          ownerIDs,
          countryIDs,
          networkIDs,
          unitTargeting,
          excludeGeometry,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    return null;
  },

  async getLocationRoots(
    { dispatch, rootGetters, rootState },
    { params = {}, cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getSupplierCountryRoots', { params, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getLocationRoots', { params, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getPackages(
    { dispatch, rootGetters, rootState },
    { params = {}, cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getPackages', { params, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getPackages', { params, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getUnits(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = false } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getUnits', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getUnits', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getUnitPacks(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getUnitPacks', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getUnitPacks', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getSites(
    { dispatch, rootGetters, rootState },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getSites', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getSites', { queryParams, cancelTokenSource, showLoader }, { root: true });
    }
    return null;
  },

  async getAvailableIDsPerFilterType(
    { dispatch, rootGetters, rootState },
    { cancelTokenSource = null, showLoader = true, packageID = null, packageGroupID = null, packageIDs = [] } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch(
        'orgApp/getAvailableIDsPerFilterType',
        {
          cancelTokenSource,
          showLoader,
          packageID,
          packageGroupID,
          packageIDs,
        },
        { root: true },
      );
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch(
        'exchApp/getAvailableIDsPerFilterType',
        {
          cancelTokenSource,
          showLoader,
          packageID,
          packageGroupID,
          packageIDs,
        },
        { root: true },
      );
    }
    return null;
  },

  async getClusteredUnitsFromTargets(
    { dispatch, rootGetters, rootState },
    { params, minLat, maxLat, minLng, maxLng, zoom, unitIDs, type, cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch(
        'orgApp/getClusteredUnitsFromTargets',
        {
          params,
          minLat,
          maxLat,
          minLng,
          maxLng,
          zoom,
          unitIDs,
          type,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch(
        'exchApp/getClusteredUnitsFromTargets',
        {
          params,
          minLat,
          maxLat,
          minLng,
          maxLng,
          zoom,
          unitIDs,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    return null;
  },

  async getClusteredSiteNetworksFromTargets(
    { dispatch, rootGetters, rootState },
    { params, minLat, maxLat, minLng, maxLng, zoom, type, cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch(
        'orgApp/getClusteredSiteNetworksFromTargets',
        {
          params,
          minLat,
          maxLat,
          minLng,
          maxLng,
          zoom,
          type,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch(
        'exchApp/getClusteredSiteNetworksFromTargets',
        {
          params,
          minLat,
          maxLat,
          minLng,
          maxLng,
          zoom,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    return null;
  },

  async getUnitsInGeofences(
    { dispatch, rootGetters, rootState },
    { params = {}, data = {}, cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch(
        'orgApp/getUnitsInGeofences',
        {
          params,
          data,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch(
        'exchApp/getUnitsInGeofences',
        {
          params,
          data,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    return null;
  },

  async getUnitIDsInGeofences(
    { dispatch, rootGetters, rootState },
    { params = {}, data = {}, cancelTokenSource = null, showLoader = true } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch(
        'orgApp/getUnitIDsInGeofences',
        {
          params,
          data,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch(
        'exchApp/getUnitIDsInGeofences',
        {
          params,
          data,
          cancelTokenSource,
          showLoader,
        },
        { root: true },
      );
    }
    return null;
  },

  async getSitesBriefUsingPost({ dispatch, rootGetters, rootState }, { data = {}, showLoader = true } = {}) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      const { site_ids: siteIDs, unit_ids: unitIDs, network_ids: networkIDs } = data;
      return dispatch(
        'orgApp/getSitesBriefUsingPost',
        {
          siteIDs,
          unitIDs,
          networkIDs,
          showLoader,
        },
        { root: true },
      );
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getSitesBriefUsingPost', { data, showLoader }, { root: true });
    }
    return null;
  },

  async getUnitsBriefUsingPost({ dispatch, rootGetters, rootState }, { data = {}, showLoader = true } = {}) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getUnitsBriefUsingPost', { unitIDs: data.unitIDs, showLoader }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch(
        'exchApp/getUnitsBriefUsingPost',
        { data: { unit_ids: data.unitIDs }, showLoader },
        { root: true },
      );
    }
    return null;
  },

  async getEstimatedDeliveryStatistics(
    { dispatch, rootGetters, rootState },
    { params, cancelTokenSource = null } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getEstimatedDeliveryStatistics', { params }, { root: true });
    } else if (isCurator(rootState)) {
      return dispatch('orgApp/getCuratedPackageEstimatedDeliveryStatistics', { params }, { root: true });
    } else if (rootGetters.hasChosenExchange) {
      return dispatch('exchApp/getEstimatedDeliveryStatistics', { params, cancelTokenSource }, { root: true });
    }
    return null;
  },

  async getEstimatedDeliveryStatisticsScreenAndSiteCount(
    { dispatch, rootGetters, rootState },
    { params, cancelTokenSource = null } = {},
  ) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getEstimatedDeliveryStatisticsScreenAndSiteCount', { params }, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch(
        'exchApp/getEstimatedDeliveryStatisticsScreenAndSiteCount',
        { params, cancelTokenSource },
        { root: true },
      );
    }
    return null;
  },

  async getTargetedUnitAndSiteIDs({ dispatch, rootGetters, rootState }, params) {
    if (isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getTargetedUnitAndSiteIDs', params, { root: true });
    }
    if (isExchangeOrCurator(rootGetters, rootState)) {
      return dispatch('exchApp/getTargetedUnitAndSiteIDs', params, { root: true });
    }
    return null;
  },

  async getCurateUnitCo2eEmissionsByVenueType({ dispatch, rootGetters, rootState }, params) {
    if (isCurator(rootState) || isSupplyOrDemand(rootGetters, rootState)) {
      return dispatch('orgApp/getCurateUnitCo2eEmissionsByVenueType', params, { root: true });
    }
    return null;
  },
};

export default actions;
